import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { deleteTrailerListing, toggleListingStatus } from '../../../services/trailerListingService';
import CalendarBlockingModal from './CalendarBlockingModal';


const ListingCard = ({ listing, onUpdate }) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return { label: 'Active', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'inactive':
        return { label: 'Inactive', color: 'bg-muted text-muted-foreground', icon: 'XCircle' };
      case 'draft':
        return { label: 'Draft', color: 'bg-warning/10 text-warning', icon: 'Edit' };
      default:
        return { label: 'Unknown', color: 'bg-muted text-muted-foreground', icon: 'AlertCircle' };
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTrailerListing(listing?.id);
      setShowDeleteModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to delete listing:', error);
      alert('Failed to delete listing. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async () => {
    setIsToggling(true);
    try {
      const newStatus = listing?.status === 'active' ? 'inactive' : 'active';
      await toggleListingStatus(listing?.id, newStatus);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const statusConfig = getStatusConfig(listing?.status);
  const primaryPhoto = listing?.photos?.[0] || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png';

  return (
    <>
      <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-smooth">
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-48 h-48 sm:h-auto overflow-hidden flex-shrink-0 relative">
          <Image
            src={primaryPhoto}
            alt={`${listing?.title} - ${listing?.trailerType} trailer`}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-3 right-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
              <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
              <span>{statusConfig?.label}</span>
            </span>
          </div>
        </div>
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                {listing?.title}
              </h3>
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="MapPin" size={16} strokeWidth={2} className="text-muted-foreground" />
                <span className="font-body text-sm text-muted-foreground">
                  {listing?.locationCity}, {listing?.locationState}
                </span>
              </div>
            </div>
            <div className="text-right ml-4">
              <p className="font-heading text-2xl font-bold text-primary">${listing?.dailyRate}</p>
              <p className="font-caption text-xs text-muted-foreground">per day</p>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-3 gap-3 mb-4 bg-muted rounded-md p-3">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Icon name="Eye" size={14} strokeWidth={2} className="text-muted-foreground" />
                <p className="font-body text-sm font-semibold text-foreground">{listing?.viewCount || 0}</p>
              </div>
              <p className="font-caption text-xs text-muted-foreground">Views</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Icon name="Calendar" size={14} strokeWidth={2} className="text-muted-foreground" />
                <p className="font-body text-sm font-semibold text-foreground">{listing?.bookingCount || 0}</p>
              </div>
              <p className="font-caption text-xs text-muted-foreground">Bookings</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Icon name="Star" size={14} strokeWidth={2} className="text-accent fill-accent" />
                <p className="font-body text-sm font-semibold text-foreground">{listing?.averageRating || 0}</p>
              </div>
              <p className="font-caption text-xs text-muted-foreground">Rating</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="default"
              size="sm"
              iconName="Edit"
              onClick={() => navigate('/trailer-listing-creation', { state: { listingId: listing?.id } })}
              className="flex-1"
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="CalendarOff"
              onClick={() => setShowCalendarModal(true)}
              className="flex-1"
            >
              Block Dates
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName={listing?.status === 'active' ? 'XCircle' : 'CheckCircle'}
              onClick={handleToggleStatus}
              loading={isToggling}
              className="flex-1"
            >
              {listing?.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              onClick={() => setShowDeleteModal(true)}
              className="flex-1 text-error hover:text-error"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Calendar Blocking Modal */}
    {showCalendarModal && (
      <CalendarBlockingModal
        listing={listing}
        onClose={() => setShowCalendarModal(false)}
        onUpdate={() => {
          setShowCalendarModal(false);
          if (onUpdate) onUpdate();
        }}
      />
    )}

    {/* Delete Confirmation Modal */}
    {showDeleteModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-card rounded-lg shadow-elevation-4 max-w-md w-full p-6">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="var(--color-error)" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Delete Listing?
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Are you sure you want to delete "{listing?.title}"? The listing will be hidden from renters but you can reactivate it later from your dashboard.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleDelete}
              loading={isDeleting}
              className="flex-1 bg-error hover:bg-error/90"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ListingCard;